import { paiseToDisplay, parseCurrencyType, type CurrencyType } from '@/lib/currency'
import type { StatementRow } from '@/data/api'

/** wallet-service / hierarchy statement row. */
export interface WalletStatementRow {
  transferId: string
  pendingId?: string | null
  kind: 'credit' | 'hold' | 'release' | 'settlement'
  direction: 'in' | 'out' | 'reserve' | 'unreserve'
  amount: string
  settledAfter: string
  heldAfter: string
  availableAfter: string
  /** Banking ledger snapshots (hierarchy bank-statement). Minor units. */
  balanceBefore?: string | null
  balanceAfter?: string | null
  timestamp: string
  /** Hierarchy statement enrichment (optional). */
  description?: string
  counterpartyId?: string | null
}

export interface WalletStatement {
  walletId: string
  currency?: string
  rows: WalletStatementRow[]
  nextCursor?: string | null
  /** Minor-unit strings when provided by hierarchy statement. */
  openingBalance?: string
  closingBalance?: string
}

function kindToConsole(
  kind: WalletStatementRow['kind'],
  direction: WalletStatementRow['direction'],
): StatementRow['kind'] {
  if (kind === 'credit') return direction === 'out' ? 'credit-out' : 'credit-in'
  return 'settlement'
}

function description(row: WalletStatementRow): string {
  switch (row.kind) {
    case 'credit':
      return row.direction === 'out' ? 'Credit sent' : 'Credit received'
    case 'hold':
      return 'Stake held'
    case 'release':
      return 'Hold released'
    case 'settlement':
      return row.direction === 'in' ? 'Settlement credit' : 'Settlement debit'
    default:
      return row.kind
  }
}

/** Nanosecond TB timestamp → ISO when possible; else keep raw / parse ISO. */
function atFromTimestamp(ts: string): string {
  if (!ts) return new Date().toISOString()
  if (ts.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(ts)) {
    const d = Date.parse(ts)
    if (Number.isFinite(d)) return new Date(d).toISOString()
  }
  const n = Number(ts)
  if (!Number.isFinite(n) || n <= 0) return new Date().toISOString()
  if (n > 1e15) return new Date(Math.floor(n / 1e6)).toISOString()
  if (n > 1e12) return new Date(n).toISOString()
  return new Date(n * 1000).toISOString()
}

export function toConsoleStatement(
  live: WalletStatement,
  currencyRaw?: string | null,
): {
  rows: StatementRow[]
  openingBalance: number
  closingBalance: number
} {
  const ccy: CurrencyType = parseCurrencyType(currencyRaw)
  const toDisplay = (minor: string | number | null | undefined) => paiseToDisplay(minor, ccy)
  const rows: StatementRow[] = (live.rows ?? []).map((row) => {
    const amountAbs = toDisplay(row.amount)
    const signed =
      row.direction === 'out' || row.direction === 'reserve' ? -amountAbs : amountAbs
    const afterMinor = row.balanceAfter ?? row.availableAfter ?? row.settledAfter
    const balanceAfter = toDisplay(afterMinor)
    const balanceBefore =
      row.balanceBefore != null && String(row.balanceBefore).trim() !== ''
        ? toDisplay(row.balanceBefore)
        : null
    return {
      id: row.transferId,
      at: atFromTimestamp(row.timestamp),
      kind: kindToConsole(row.kind, row.direction),
      description: row.description?.trim() || description(row),
      counterparty: row.counterpartyId ?? null,
      amount: signed,
      balanceBefore,
      balanceAfter,
      balance: balanceAfter,
      ref: row.transferId,
    }
  })

  const closingBalance =
    live.closingBalance != null
      ? toDisplay(live.closingBalance)
      : (rows[0]?.balanceAfter ?? 0)
  const oldest = rows[rows.length - 1]
  const openingBalance =
    live.openingBalance != null
      ? toDisplay(live.openingBalance)
      : oldest?.balanceBefore != null
        ? oldest.balanceBefore
        : oldest
          ? oldest.balanceAfter - oldest.amount
          : closingBalance

  return { rows, openingBalance, closingBalance }
}
